import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Card, CardBody } from "reactstrap";


export const FilterContainer = ({ filters, data, onFilterChange }) => {
    // Track all active filters in a single state object
    const [activeFilters, setActiveFilters] = useState({});
    const initializedRef = useRef(false);

    // Initialize filters once when component mounts or when filters change
    useEffect(() => {
        if (filters.length > 0) {
            const initialFilters = filters.reduce((acc, filter) => ({
                ...acc,
                [filter.field]: {
                    value: filter.getInitialState()?.value,
                    isActive: false
                }
            }), {});

            setActiveFilters(initialFilters);

            // Only reset to unfiltered data on initial load
            if (!initializedRef.current) {
                onFilterChange(data);
                initializedRef.current = true;
            }
        }
    }, [filters]);

    // Apply all filters whenever any filter changes
    const applyFilters = useCallback(() => {
        let filteredResult = data;

        // Apply each active filter sequentially
        Object.entries(activeFilters).forEach(([field, filterState]) => {
            if (filterState && filterState.isActive) {
                const filter = filters.find(f => f.field === field);
                if (filter) {
                    filteredResult = filter.applyFilter(filteredResult, filterState.value);
                }
            }
        });

        onFilterChange(filteredResult);
    }, [data, filters, activeFilters, onFilterChange]);

    // Update filter state and apply filters
    const handleFilterChange = useCallback((field, value) => {
        setActiveFilters(prev => ({
            ...prev,
            [field]: {
                value,
                isActive: true
            }
        }));
    }, []);

    // Effect to apply filters whenever activeFilters changes
    useEffect(() => {
        if (Object.keys(activeFilters).length > 0 && initializedRef.current) {
            applyFilters();
        }
    }, [activeFilters, applyFilters]);

    // Handle reset button click
    const handleReset = useCallback(() => {
        // Reset to initial state
        const resetFilters = filters.reduce((acc, filter) => ({
            ...acc,
            [filter.field]: {
                value: filter.getInitialState()?.value,
                isActive: false
            }
        }), {});

        setActiveFilters(resetFilters);
        onFilterChange(data);
    }, [filters, data, onFilterChange]);

    return (
        <Card className="mb-3">
            <CardBody>
                <div className="d-flex flex-wrap gap-4 align-items-center">
                    {filters.map(filter => (
                        <div key={filter.field} style={{ height: '100%' }}>
                            {filter.renderFilter({
                                value: activeFilters[filter.field]?.value ?? filter.getInitialState()?.value,
                                onChange: (value) => handleFilterChange(filter.field, value)
                            })}
                        </div>
                    ))}
                    <button
                        className="btn btn-danger ms-auto"
                        onClick={handleReset}
                    >
                        Reset Filters
                    </button>
                </div>
            </CardBody>
        </Card>
    );
};
